import React, { useEffect, useRef, useState } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { EVENTS, Joyride, STATUS } from 'react-joyride';

const TOUR_VERSION = 'v1';

const steps = [
  {
    target: '[data-tour="ielts-header-subscription"]',
    skipScroll: true,
    title: 'Your IELTS access',
    content: 'Check your current access or open the available subscription options from here.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="ielts-header-controls"]',
    skipScroll: true,
    title: 'Your account controls',
    content: 'View updates, switch your exam settings, and open your profile from these controls.',
    placement: 'bottom-end',
  },
  {
    target: '[data-tour="ielts-dashboard-heading-focus"]',
    skipScroll: true,
    title: 'Your IELTS dashboard',
    content: 'This is your home for tracking IELTS progress, goals, results, and test activity.',
    placement: 'bottom-start',
  },
  {
    target: '[data-tour="ielts-dashboard-scores"]',
    title: 'Track your progress',
    content: 'Review your average band score, set your target score, and keep your exam date up to date.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="ielts-dashboard-results"]',
    title: 'Your latest results',
    content: 'Your four latest IELTS attempts appear here. Select any result to open its detailed report.',
    placement: 'right',
  },
  {
    target: '[data-tour="ielts-dashboard-community"]',
    title: 'Learn with the community',
    content: 'Use the community area to discuss IELTS preparation and learn with other students.',
    placement: 'left',
  },
  {
    target: '[data-tour="ielts-dashboard-navigation-actions"]',
    skipScroll: true,
    title: 'Choose your next step',
    content: 'Open your results, practise one IELTS section, or start a complete full mock test from here.',
    placement: 'right-start',
  },
];

const getStorageKey = (userId) => `swiftuni:ielts-dashboard-tour:${TOUR_VERSION}:${userId}`;

const IeltsDashboardTour = ({ userId, ready }) => {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [run, setRun] = useState(false);
  const [tourInstance, setTourInstance] = useState(0);
  const autoStartChecked = useRef(false);

  useEffect(() => {
    if (!isDesktop || !ready || !userId || autoStartChecked.current) return;

    autoStartChecked.current = true;
    if (window.localStorage.getItem(getStorageKey(userId)) !== 'completed') {
      setRun(true);
    }
  }, [isDesktop, ready, userId]);

  if (!isDesktop) return null;

  const handleTourEvent = ({ type, status }) => {
    if (type !== EVENTS.TOUR_END) return;
    if (![STATUS.FINISHED, STATUS.SKIPPED].includes(status)) return;

    if (userId) {
      window.localStorage.setItem(getStorageKey(userId), 'completed');
    }
    setRun(false);
  };

  const handleReplay = () => {
    setTourInstance((current) => current + 1);
    setRun(true);
  };

  return (
    <>
      <Tooltip title="Take dashboard tour">
        <IconButton
          aria-label="Take dashboard tour"
          size="small"
          onClick={handleReplay}
          sx={{
            width: 32,
            height: 32,
            color: '#7351e9',
            border: '1px solid rgba(115, 81, 233, 0.24)',
            backgroundColor: '#faf8ff',
            '&:hover': {
              backgroundColor: '#f1ebff',
              borderColor: 'rgba(115, 81, 233, 0.45)',
            },
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Tooltip>

      <Joyride
        key={tourInstance}
        continuous
        run={run}
        steps={steps}
        onEvent={handleTourEvent}
        locale={{
          back: 'Back',
          last: 'Start',
          next: 'Next',
          nextWithProgress: 'Next ({current} of {total})',
          skip: 'Skip',
        }}
        options={{
          backgroundColor: '#ffffff',
          blockTargetInteraction: true,
          buttons: ['back', 'primary', 'skip'],
          dismissKeyAction: false,
          overlayClickAction: false,
          overlayColor: 'rgba(28, 20, 48, 0.68)',
          primaryColor: '#7351e9',
          scrollDuration: 420,
          scrollOffset: 100,
          showProgress: true,
          skipBeacon: true,
          spotlightPadding: 8,
          spotlightRadius: 12,
          textColor: '#2d2547',
          width: 360,
          zIndex: 16000,
        }}
        styles={{
          tooltip: {
            borderRadius: 12,
            boxShadow: '0 18px 48px rgba(33, 25, 54, 0.22)',
            padding: 4,
          },
          tooltipTitle: {
            fontSize: 17,
            fontWeight: 800,
            letterSpacing: 0,
          },
          tooltipContent: {
            fontSize: 14,
            lineHeight: 1.55,
            padding: '14px 16px 10px',
          },
          buttonPrimary: {
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 800,
            padding: '8px 16px',
          },
          buttonBack: {
            color: '#675d78',
            fontSize: 13,
            fontWeight: 700,
          },
          buttonSkip: {
            color: '#675d78',
            fontSize: 13,
            fontWeight: 700,
          },
        }}
      />
    </>
  );
};

export default IeltsDashboardTour;
